import React, { useState } from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
  IconButton,
  Chip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Register Chart.js components
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement, 
  ArcElement,
  Title, 
  Tooltip, 
  Legend
);

const Analytics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Data for Occupancy Rate Chart
  const occupancyData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
    datasets: [
      {
        label: "Occupancy Rate (%)",
        data: [75, 80, 70, 85, 90, 88, 92],
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}20`,
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Data for Revenue Trends Chart
  const revenueData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
    datasets: [
      {
        label: "Revenue ($)",
        data: [5000, 6000, 5500, 7000, 8000, 7500, 8500],
        backgroundColor: theme.palette.secondary.main,
        hoverBackgroundColor: theme.palette.secondary.dark,
        borderColor: theme.palette.secondary.main,
        borderWidth: 2,
        barThickness: 20,
        borderRadius: 5,
      },
    ],
  };

  // Data for Guest Source
  const guestSourceData = {
    labels: ['Direct', 'Booking.com', 'Expedia', 'Airbnb', 'Others'],
    datasets: [
      {
        data: [20, 35, 25, 15, 5],
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.secondary.main,
          theme.palette.success.main,
          theme.palette.warning.main,
          theme.palette.grey[500],
        ],
        borderWidth: 1,
      },
    ],
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: "top",
    },
    tooltip: {
      enabled: true,
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        boxShadow: theme.shadows[3],
      callbacks: {
        label: (context) => `${context.dataset.label}: ${context.parsed.y}`,
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
    },
    y: {
      beginAtZero: true,
      grid: {
        display: true,
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
    },
  },
};

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
    cutout: '70%',
  };

  const cardAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
          Analytics & Reports
        </Typography>
      </motion.div>
      
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          textColor="primary"
          indicatorColor="primary"
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" />
          <Tab label="Occupancy" />
          <Tab label="Revenue" />
          <Tab label="Guest Insights" />
        </Tabs>
      </Box>

      {/* KPI Summary Cards */}
      <Box sx={{ display: tabValue === 0 ? 'block' : 'none' }}>
        <Grid container spacing={3} mb={4}>
          {[
            { title: "Average Occupancy", value: "86%", change: "+12%", icon: <TrendingUpIcon color="success" /> },
            { title: "Revenue YTD", value: "₹47,500", change: "+15%", icon: <TrendingUpIcon color="success" /> },
            { title: "Average Rating", value: "4.8/5", change: "+0.3", icon: <TrendingUpIcon color="success" /> },
            { title: "Booking Conversion", value: "32%", change: "-3%", icon: <TrendingDownIcon color="error" /> }
          ].map((kpi, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                variants={cardAnimation}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card elevation={2} sx={{ height: '100%' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {kpi.title}
                      </Typography>
                      <IconButton size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="h4" component="div" fontWeight="bold">
                      {kpi.value}
                    </Typography>
                    <Box display="flex" alignItems="center" mt={1}>
                      {kpi.icon}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          ml: 0.5, 
                          color: kpi.change.startsWith('+') ? 'success.main' : 'error.main' 
                        }}
                      >
                        {kpi.change} vs. last year
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
        
        <Grid container spacing={3}>
          {/* Occupancy Chart */}
          <Grid item xs={12} md={8}>
            <motion.div
              variants={cardAnimation}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Card elevation={2}>
                <CardHeader 
                  title="Occupancy Trends" 
                  action={
                    <IconButton>
                      <MoreVertIcon />
                    </IconButton>
                  }
                />
                <Divider />
                <CardContent>
                  <Box height={300}>
                    <Line data={occupancyData} options={chartOptions} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          
          {/* Guest Source */}
          <Grid item xs={12} md={4}>
            <motion.div
              variants={cardAnimation}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <Card elevation={2} sx={{ height: '100%' }}>
                <CardHeader title="Booking Sources" />
                <Divider />
                <CardContent>
                  <Box height={280} display="flex" justifyContent="center" alignItems="center">
                    <Doughnut data={guestSourceData} options={doughnutOptions} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Box>

      {/* Occupancy Tab Content */}
      <Box sx={{ display: tabValue === 1 ? 'block' : 'none' }}>
        <Card elevation={2}>
          <CardHeader title="Occupancy Rate" />
          <CardContent>
            <Box height={400}>
              <Line data={occupancyData} options={chartOptions} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Revenue Tab Content */}
      <Box sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
        <Card elevation={2}>
          <CardHeader title="Revenue Trends" />
          <CardContent>
            <Box height={400}>
              <Bar data={revenueData} options={chartOptions} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Guest Insights Tab Content */}
      <Box sx={{ display: tabValue === 3 ? 'block' : 'none' }}>
        <Typography variant="h6" gutterBottom mb={3}>Guest Behavior Insights</Typography>
        <Grid container spacing={3}>
          {[
            { title: "Peak Booking Periods", content: "July and December are the busiest months.", color: "primary" },
            { title: "Most Common Review Topics", content: "Cleanliness and staff service are frequently mentioned.", color: "secondary" },
            { title: "Average Stay Duration", content: "Guests typically stay for 3 nights.", color: "info" },
            { title: "Repeat Customers", content: "25% of bookings are from returning guests.", color: "success" },
            { title: "Top Room Type", content: "Deluxe Double Room makes up 40% of bookings.", color: "warning" },
            { title: "Cancellation Rate", content: "8% of bookings get canceled before check-in.", color: "error" }
          ].map((insight, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                variants={cardAnimation}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper 
                  elevation={2}
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    borderTop: 3, 
                    borderColor: `${insight.color}.main` 
                  }}
                >
                  <Box mb={1}>
                    <Chip 
                      label={insight.title}
                      color={insight.color}
                      size="small"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                  <Typography variant="body1">{insight.content}</Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default Analytics;